import { Machine } from "./Connections";
// Note: React hooks (useAppInsightsContext) cannot be used inside this class.
// Pass an AppInsights instance from a React component instead.

type TransformFn = (
    settings: any,
    originalSetting: any,
    destinationSetting: any
) => { originalNewSetting: any; destinationNewSetting: any };

export default class SettingsMigration {
    // Stored migration configuration
    originalPath: string;
    destinationPath: string;
    migrationName: string;
    transformFunction?: TransformFn;
    originalMachinePath?: string;

    constructor(
        originalPath: string,
        destinationPath: string,
        migrationName: string,
        transformFunction?: TransformFn,
        originalMachinePath?: string
    ) {
        this.originalPath = originalPath;
        this.destinationPath = destinationPath;
        this.migrationName = migrationName;
        this.transformFunction = transformFunction;
        this.originalMachinePath = originalMachinePath;
    }

    private getSettingFromSegments = (root: any, segments: string[]) => {
        let current = root;
        for (const segment of segments) {
            if (current == null) return undefined;
            current = current[segment];
        }
        return current;
    };

    private removeOriginalMachineSetting = async (machine: Machine): Promise<void> => {
        const removeBody: any = {
            Requests: {
                SetSetting: {
                    Path: this.originalMachinePath,
                    Remove: true
                }
            }
        };
        const removeResponse: any = await machine.connection.post(removeBody);
        if (removeResponse?.Responses?.SetSetting?.code !== "0") {
            throw new Error(`Failed to remove legacy setting at ${this.originalMachinePath}`);
        }
    };

    private migrateFromMachinePath = async (settings: any, machine: Machine, userSettings?: any): Promise<void> => {
        const markerPath = `/Settings/User/Migrations/${this.destinationPath}`;
        const markerSegments = ['Migrations', ...this.destinationPath.split('/')];
        const hasUserSettingsSnapshot = userSettings != null;
        const markerValue = hasUserSettingsSnapshot
            ? this.getSettingFromSegments(userSettings, markerSegments)
            : (await machine.connection.get(`GetSettings?Path=${markerPath}`) as any)?.Responses?.GetSettings?.value;

        const originalPathSegments = this.originalMachinePath!
            .replace(/^\/Settings\/User\//, '')
            .split('/');
        const legacyValue = hasUserSettingsSnapshot
            ? this.getSettingFromSegments(userSettings, originalPathSegments)
            : (await machine.connection.get(`GetSettings?Path=${this.originalMachinePath}`) as any)?.Responses?.GetSettings?.value;

        if (markerValue != null) {
            if (legacyValue != null) {
                await this.removeOriginalMachineSetting(machine);
                console.log(`Removed legacy setting at ${this.originalMachinePath}.`);
            }
            console.log(`Migration marker already present for ${this.migrationName} at ${markerPath}; skipping.`);
            return;
        }

        console.log(`No migration marker found for ${this.migrationName} at ${markerPath}; proceeding with migration.`);

        const destinationSetting = this.getSettingFromSegments(settings, this.destinationPath.split('/'));
        if (!destinationSetting?.Set) {
            throw new Error(`Path ${this.destinationPath} not found in settings`);
        }

        const destinationValue = String(legacyValue).toLowerCase() === 'true' ? 'Automatic' : 'None';

        await destinationSetting.Set(destinationValue);
        const postBody: any = {
            Requests: {
                SetSetting: {
                    Path: markerPath,
                    Value: "true",
                    Type: "string"
                }
            }
        };
        const markerWriteResponse: any = await machine.connection.post(postBody);
        if (markerWriteResponse?.Responses?.SetSetting?.code !== "0") {
            throw new Error(`Failed to write migration marker at ${markerPath}`);
        }

        if (legacyValue != null) {
            await this.removeOriginalMachineSetting(machine);
            console.log(`Removed legacy setting at ${this.originalMachinePath}.`);
        }
    };


    
    migrate = async (
        settings: any,
        machine: Machine,
        appInsights: { trackException: (data: any) => void },
        userSettings?: any
    ): Promise<void> => {
        if (this.originalMachinePath) {
            try {
                await this.migrateFromMachinePath(settings, machine, userSettings);
            } catch (error) {
                appInsights?.trackException({
                    exception: error instanceof Error ? error : new Error(String(error)),
                    severityLevel: 3,
                    properties: {
                        migrationName: this.migrationName,
                        originalPath: this.originalMachinePath,
                        destinationPath: this.destinationPath,
                        machineVersion: machine.version,
                        machineName: machine.machineName
                    }
                });
            }
            return;
        }

        const migrationMarkerRelativePath = `User/Migrations/${this.destinationPath}`; // relative to /Settings

        // Helper to safely traverse nested settings path
        // Build marker path segments e.g. ['User','Migrations', ...destinationPath.split('/')]
        const markerSegments = ['User', 'Migrations', ...this.destinationPath.split('/')];
        const existingMarker = this.getSettingFromSegments(settings, markerSegments);
        if (existingMarker !== undefined && existingMarker !== null) {
            // Migration already performed (marker exists) — exit early
            console.log(`Migration marker already present for ${this.migrationName} at ${migrationMarkerRelativePath}; skipping.`);
            return;
        }


        console.log(`No migration marker found locally for ${this.migrationName}; proceeding with migration.`);

        // Split paths into array segments
        const oldPathSegments = this.originalPath.split('/');
        const newPathSegments = this.destinationPath.split('/');
        
        // Get value from old path
        let originalSetting = settings;
        for (const segment of oldPathSegments) {
            if (originalSetting === undefined || originalSetting === null) {
                console.log(`Path ${this.originalPath} not found in settings`);
                appInsights?.trackException({
                    exception: new Error(`Path ${this.originalPath} not found in settings during migration ${this.migrationName}`),
                    severityLevel: 3, // Error
                    properties: {
                        migrationName: this.migrationName,
                        originalPath: this.originalPath,
                        destinationPath: this.destinationPath,
                        machineVersion: machine.version,
                        machineName: machine.machineName
                    }
                })
                return;
            }
            originalSetting = originalSetting[segment];
        }
        console.log(`Value at ${this.originalPath}:`, originalSetting);

        let destinationSetting = settings;
        for (const segment of newPathSegments) {
            if (destinationSetting === undefined || destinationSetting === null) {
                console.log(`Path ${this.destinationPath} not found in settings`);
                appInsights?.trackException({
                    exception: new Error(`Path ${this.destinationPath} not found in settings`),
                    severityLevel: 3, // Error
                    properties: {
                        migrationName: this.migrationName,
                        originalPath: this.originalPath,
                        destinationPath: this.destinationPath,
                        machineVersion: machine.version,
                        machineName: machine.machineName
                    }
                });
                return;
            }
            destinationSetting = destinationSetting[segment];
        }

        // Extract the value from the old setting
        let destinationNewValue = originalSetting.value;

        // Apply transform function if provided
        if (this.transformFunction) {
            const result = this.transformFunction(settings, originalSetting, destinationSetting);
            await originalSetting.Set(result.originalNewSetting.value);
            destinationNewValue = result.destinationNewSetting.value;
        }

        try {
            await destinationSetting.Set(destinationNewValue);

            // Mark migration as completed on the machine
            const postBody: any = {
                Requests: {
                    SetSetting: {
                        Path: `/Settings/${migrationMarkerRelativePath}`,
                        Value: "true",
                        Type: "boolean"
                    },
                },
            };
            await machine.connection.post(postBody);
            console.log(`Migration ${this.migrationName} from ${this.originalPath} to ${this.destinationPath} completed and marked at ${migrationMarkerRelativePath}`);
        } catch (error) {
            console.error(`Failed to migrate ${this.migrationName} from ${this.originalPath} to ${this.destinationPath}:`, error);
            appInsights?.trackException({
                exception: error instanceof Error ? error : new Error(String(error)),
                severityLevel: 3, // Error
                properties: {
                    migrationName: this.migrationName,
                    originalPath: this.originalPath,
                    destinationPath: this.destinationPath,
                    machineVersion: machine.version,
                    machineName: machine.machineName
                }
            });
        }
    };
}